<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRowFitCount } from '@/composables/use-row-fit-count';

const props = defineProps({
  title: { type: String, required: true },
  items: { type: Array, required: true },
  component: { type: Object, required: true },
  itemProps: { type: Object, default: () => ({}) },
  viewAllRoute: { type: Object, required: true },
});

const { t } = useI18n();

const row = ref(null);
const fitCount = useRowFitCount(row);

const visibleItems = computed(() => props.items.slice(0, fitCount.value));
</script>

<template>
  <section class="global-search-section">
    <div class="global-search-section__head">
      <h3 class="global-search-section__title">
        {{ title }}
        <span class="global-search-section__count">{{ items.length }}</span>
      </h3>
      <router-link class="global-search-section__view-all" :to="viewAllRoute">{{ t('search.viewAll') }}</router-link>
    </div>
    <div ref="row" class="global-search-section__row">
      <div v-for="item in visibleItems" :key="item.id" class="global-search-section__item">
        <component :is="component" :item="item" v-bind="itemProps" />
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.global-search-section {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 10px;
    border-bottom: 1px solid $black80;
  }

  &__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: $white;
  }

  &__count {
    margin-left: 8px;
    font-size: 0.95rem;
    font-weight: 400;
    color: $gray50;
  }

  &__view-all {
    padding: 6px 12px;
    border: 1px solid $black80;
    border-radius: 4px;
    background: $black30;
    color: $text-primary;
    font-size: 0.8rem;
    font-weight: 600;
    white-space: nowrap;

    &::after {
      content: ' →';
    }

    &:hover,
    &:focus-visible {
      border-color: $text-primary;
    }
  }

  &__row {
    display: flex;
    gap: 10px;
  }

  &__item {
    flex-shrink: 0;
    width: 250px;
    height: 150px;
    padding: 5px;
  }
}
</style>
